// components/Analytics.jsx
import React, { useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Skeleton,
    Stack,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    LinearProgress,
    Pagination as MuiPagination
} from '@mui/material';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Colors
} from 'chart.js';
import {
    TrendingUp,
    TrendingDown,
    Person,
    ShowChart,
    PieChartOutline,
    Leaderboard,
    Inventory
} from '@mui/icons-material';
import { useFetch } from '../../hooks';
import { transformToBTC } from '../../utils/helpers';
import { useNavigate } from 'react-router-dom';
import { useBitcoinPrice } from '../../state/context/bitcoin.context';
import { useTranslation } from 'react-i18next';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Colors
);

// Utility
const truncateId = (id, n = 5) =>
    !id || id.length <= 2 * n + 3
        ? id
        : `${ id.slice(0, n) }...${ id.slice(-n) }`;

const formatNumber = (num) =>
    typeof num !== 'number' || isNaN(num) ? 'N/A' : num.toLocaleString();

const formatPnl = (pnlBtc) => {
    const v = parseFloat(pnlBtc);
    if (isNaN(v)) return 'N/A';
    const txt = transformToBTC(v);
    return v >= 0 ? `+${ txt }` : txt;
};

// KPI StatCard
const StatCard = ({ title, value, icon, accent, usd }) => (
    <Paper
        variant="outlined"
        sx={{
            bgcolor: '#1E1E1E',
            color: '#FFF',
            borderRadius: 2,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: '90%',
            boxShadow: 1,
            borderLeft: `4px solid ${ accent }`,
        }}
    >
        <Box sx={{ mb: 1 }}>{icon}</Box>
        <Typography variant="body2" sx={{ color: '#BBB' }}>
            {title}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 'medium', mt: 0.5 }}>
            {value}
        </Typography>
        {usd && (
            <Typography variant="body2" sx={{ color: '#AAA', mt: 0.5 }}>
                ${usd}
            </Typography>
        )}
    </Paper>
);

// Reusable AnalyticsTable with optional data bars
const AnalyticsTable = ({ title, data, columns, navigate, icon }) => {
    const { t } = useTranslation();
    const maxValues = useMemo(() => {
        const m = {};
        columns.forEach((col) => {
            if (col.dataBar) {
                m[col.field] = Math.max(
                    0,
                    ...data.map((r) => Math.abs(r[col.field] || 0))
                );
            }
        });
        return m;
    }, [data, columns]);

    const renderCell = (row, col) => {
        const raw = row[col.field];
        const display = col.renderCell
            ? col.renderCell(row)
            : raw;
        if (col.dataBar && typeof raw === 'number' && maxValues[col.field] > 0) {
            const pct = (Math.abs(raw) / maxValues[col.field]) * 100;
            let barColor = '#3e95cd';
            if (col.field.toLowerCase().includes('pnl'))
                barColor = raw >= 0 ? '#66bb6a' : '#ef5350';
            if (col.field.toLowerCase().includes('volume'))
                barColor = '#29b6f6';

            return (
                <Box sx={{ position: 'relative', width: '100%' }}>
                    <Box sx={{ position: 'relative', zIndex: 2 }}>{display}</Box>
                    <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                            position: 'absolute',
                            bottom: 2,
                            left: 0,
                            width: '100%',
                            height: 8,
                            bgcolor: 'rgba(255,255,255,0.1)',
                            '& .MuiLinearProgress-bar': {
                                bgcolor: barColor,
                            },
                        }}
                    />
                </Box>
            );
        }
        return display;
    };

    return (
        <Paper
            variant="outlined"
            sx={{ bgcolor: '#1E1E1E', color: '#FFF', borderRadius: 2, p: 2 }}
        >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                {icon} <Typography variant="h6">{title}</Typography>
            </Stack>
            {data.length === 0 ? (
                <Typography sx={{ color: '#BBB', fontStyle: 'italic' }}>
                    {t('common.noDataAvailable')}
                </Typography>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'rgba(255,255,255,0.08)' }}>
                            {columns.map((col) => (
                                <TableCell
                                    key={col.field}
                                    align={col.align || 'left'}
                                    sx={{ color: '#BBB', fontWeight: 'bold' }}
                                >
                                    {col.headerName}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((row, i) => (
                            <TableRow
                                key={i}
                                hover
                                onClick={() =>
                                    navigate && row.user
                                        ? window.open(`/users/${ row.user }`)
                                        : null
                                }
                                sx={{
                                    cursor: navigate && row.user ? 'pointer' : 'default',
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                    },
                                }}
                            >
                                {columns.map((col) => (
                                    <TableCell
                                        key={col.field}
                                        align={col.align || 'left'}
                                        sx={{ color: '#FFF' }}
                                    >
                                        {renderCell(row, col)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Paper>
    );
};

const Analytics = ({ tokenId }) => {
    const { t } = useTranslation();
    const { price: btcPrice } = useBitcoinPrice();
    const navigate = useNavigate();
    const { data, loading, error } = useFetch(
        `/tokens/${ tokenId }/analytics`,
        [tokenId]
    );

    // Extract arrays safely
    const topVol = data?.topVolumeTraders || [];
    const holders = data?.topHolders || [];
    const prof = data?.mostProfitableTraders || [];
    const loss = data?.biggestLoserTraders || [];
    const cnt = data?.topTradersByCount || [];

    // KPI calculations
    const kpi = useMemo(() => {
        const sumVol = topVol.reduce((s, t) => s + (t.totalVolumeBTC || 0), 0);
        const usdVol = (sumVol / 1e11) * btcPrice;
        const topHolder = holders[0]?.currentHolding || 0;
        const topProfit = prof[0]?.realizedPnlOnSoldBTC || 0;
        const topLoss = loss[0]?.realizedPnlOnSoldBTC || 0;
        return {
            sumVol,
            usdVol,
            topHolder,
            topProfit,
            topLoss,
            topProfitUsd: (topProfit / 1e11) * btcPrice,
            topLossUsd: (topLoss / 1e11) * btcPrice,
        };
    }, [topVol, holders, prof, loss, btcPrice]);

    // Chart data
    const volChart = {
        labels: topVol.map((t) => truncateId(t.user)),
        datasets: [
            {
                label: 'Volume (BTC)',
                data: topVol.map((t) => t.totalVolumeBTC / 1e11),
            },
        ],
    };
    const holdersChart = useMemo(() => {
        const max = 6;
        let slice = holders;
        let other = 0;
        if (holders.length > max) {
            slice = holders.slice(0, max - 1);
            other = holders.slice(max - 1).reduce((s, h) => s + h.currentHolding, 0);
        }
        const lbls = slice.map((h) => truncateId(h.user));
        const dataPts = slice.map((h) => h.currentHolding);
        if (other > 0) {
            lbls.push('Others');
            dataPts.push(other);
        }
        return {
            labels: lbls,
            datasets: [{ data: dataPts }],
        };
    }, [holders]);

    const baseOpts = {
        maintainAspectRatio: false,
        plugins: {
            legend: { labels: { color: '#EEE' } },
            tooltip: { titleColor: '#FFF', bodyColor: '#FFF' },
        },
        scales: {
            x: { ticks: { color: '#EEE' }, grid: { color: 'rgba(255,255,255,0.1)' } },
            y: { ticks: { color: '#EEE' }, grid: { color: 'rgba(255,255,255,0.1)' } },
        },
    };

    if (error) {
        return (
            <Paper
                variant="outlined"
                sx={{ p: 3, bgcolor: '#C62828', color: '#FFF', borderRadius: 2 }}
            >
                <Typography>{t('analytics.errorLoadingAnalytics')}</Typography>
            </Paper>
        );
    }

    return (
        <Box sx={{ p: 2, bgcolor: '#121212' }}>
            <Typography
                variant="h5"
                sx={{ color: '#FFF', fontWeight: 500, mb: 3 }}
            >
                {t('analytics.traderAnalytics')}
            </Typography>

            {/* KPIs */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={6} sm={3}>
                    <StatCard
                        title={t('analytics.topVolume')}
                        value={transformToBTC(kpi.sumVol)}
                        icon={<ShowChart fontSize="large" sx={{ color: '#29b6f6' }} />}
                        accent="#29b6f6"
                        usd={kpi.usdVol.toFixed(0)}
                    />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <StatCard
                        title={t('analytics.largestHolder')}
                        value={`${ formatNumber(kpi.topHolder / 1e11) } `}
                        icon={<Inventory fontSize="large" sx={{ color: '#ab47bc' }} />}
                        accent="#ab47bc"
                    />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <StatCard
                        title={t('analytics.highestProfit')}
                        value={formatPnl(kpi.topProfit)}
                        icon={<TrendingUp fontSize="large" sx={{ color: '#66bb6a' }} />}
                        accent="#66bb6a"
                        usd={kpi.topProfitUsd.toFixed(0)}
                    />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <StatCard
                        title={t('analytics.biggestLoss')}
                        value={formatPnl(kpi.topLoss)}
                        icon={<TrendingDown fontSize="large" sx={{ color: '#ef5350' }} />}
                        accent="#ef5350"
                        usd={kpi.topLossUsd.toFixed(0)}
                    />
                </Grid>
            </Grid>

            {/* Charts & Tables */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                    <Paper
                        variant="outlined"
                        sx={{ p: 2, bgcolor: '#1E1E1E', borderRadius: 2, height: '99%' }}
                    >
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Leaderboard sx={{ color: '#FFF' }} />
                                <Typography sx={{ color: '#FFF', fontWeight: 500 }}>
                                    {t('analytics.topVolumeTraders')}
                                </Typography>
                            </Stack>
                            <Box sx={{ height: 300 }}>
                                {loading ? (
                                    <Skeleton
                                        variant="rectangular"
                                        height="100%"
                                        sx={{ bgcolor: 'grey.900' }}
                                    />
                                ) : topVol.length > 0 ? (
                                    <Bar data={volChart} options={baseOpts} />
                                ) : (
                                    <Typography sx={{ color: '#BBB' }}>
                                        {t('common.noDataAvailable')}
                                    </Typography>
                                )}
                            </Box>
                            <AnalyticsTable
                                title={t('common.details')}
                                data={topVol}
                                columns={[
                                    {
                                        field: 'user',
                                        headerName: t('table.user'),
                                        renderCell: (r) => truncateId(r.user),
                                    },
                                    {
                                        field: 'totalVolumeBTC',
                                        headerName: t('table.volume'),
                                        align: 'right',
                                        dataBar: true,
                                        renderCell: (r) => transformToBTC(r.totalVolumeBTC),
                                    },
                                ]}
                                navigate={navigate}
                                icon={<Leaderboard sx={{ color: '#FFF' }} />}
                            />
                        </Stack>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper
                        variant="outlined"
                        sx={{ p: 2, bgcolor: '#1E1E1E', borderRadius: 2, height: '99%' }}
                    >
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <PieChartOutline sx={{ color: '#FFF' }} />
                                <Typography sx={{ color: '#FFF', fontWeight: 500 }}>
                                    {t('analytics.topHolders')}
                                </Typography>
                            </Stack>
                            <Box
                                sx={{
                                    height: 300,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                {loading ? (
                                    <Skeleton
                                        variant="circular"
                                        width={200}
                                        height={200}
                                        sx={{ bgcolor: 'grey.900' }}
                                    />
                                ) : holders.length > 0 ? (
                                    <Doughnut
                                        data={holdersChart}
                                        options={{
                                            ...baseOpts,
                                            cutout: '60%',
                                            plugins: {
                                                ...baseOpts.plugins,
                                                legend: { position: 'right', labels: { color: '#EEE' } },
                                            },
                                        }}
                                    />
                                ) : (
                                    <Typography sx={{ color: '#BBB' }}>
                                        {t('analytics.noHolderData')}
                                    </Typography>
                                )}
                            </Box>
                            <AnalyticsTable
                                title={t('common.details')}
                                data={holders}
                                columns={[
                                    {
                                        field: 'user',
                                        headerName: t('table.user'),
                                        renderCell: (r) => truncateId(r.user),
                                    },
                                    {
                                        field: 'currentHolding',
                                        headerName: t('table.holding'),
                                        align: 'right',
                                        dataBar: true,
                                        renderCell: (r) => formatNumber(r.currentHolding / 1e11),
                                    },
                                ]}
                                navigate={navigate}
                                icon={<PieChartOutline sx={{ color: '#FFF' }} />}
                            />
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <AnalyticsTable
                        title={t('analytics.mostProfitable')}
                        data={prof}
                        columns={[
                            {
                                field: 'user',
                                headerName: t('table.user'),
                                renderCell: (r) => truncateId(r.user),
                            },
                            {
                                field: 'realizedPnlOnSoldBTC',
                                headerName: t('table.profitLoss'),
                                align: 'right',
                                dataBar: true,
                                renderCell: (r) => formatPnl(r.realizedPnlOnSoldBTC),
                            },
                        ]}
                        navigate={navigate}
                        icon={<TrendingUp sx={{ color: '#66bb6a' }} />}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <AnalyticsTable
                        title={t('analytics.biggestLosers')}
                        data={loss}
                        columns={[
                            {
                                field: 'user',
                                headerName: t('table.user'),
                                renderCell: (r) => truncateId(r.user),
                            },
                            {
                                field: 'realizedPnlOnSoldBTC',
                                headerName: t('table.profitLoss'),
                                align: 'right',
                                dataBar: true,
                                renderCell: (r) => formatPnl(r.realizedPnlOnSoldBTC),
                            },
                        ]}
                        navigate={navigate}
                        icon={<TrendingDown sx={{ color: '#ef5350' }} />}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <AnalyticsTable
                        title={t('analytics.mostActive')}
                        data={cnt}
                        columns={[
                            {
                                field: 'user',
                                headerName: t('table.user'),
                                renderCell: (r) => truncateId(r.user),
                            },
                            {
                                field: 'tradeCount',
                                headerName: t('table.trades'),
                                align: 'right',
                                dataBar: false,
                                renderCell: (r) => formatNumber(r.tradeCount),
                            },
                        ]}
                        navigate={navigate}
                        icon={<Person sx={{ color: '#29b6f6' }} />}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default Analytics;
